import { useTranslation } from 'react-i18next';
import {
  MarketAccessory,
  MarketButton,
  MarketButtonGroup,
  MarketDialog,
  MarketDivider,
} from '@market/react';
import { usePaymentContext } from 'typescript/context/payment_context';
import { ReactComponent as DisclosureIcon } from 'images/modal_headers/financing-disclosure.svg';
import { getLocaleString } from 'typescript/utilities/locales';
import { getDomain } from 'typescript/utilities/environment';
import styles from 'typescript/apps/checkout_v3/components/payment/pay_in_installments/disclosure_modal.module.scss';
import { onCheckoutPageClick } from 'typescript/utilities/tracking';
import { CHECKOUT_PAGE_CLICK_EVENTS } from 'typescript/constants/tracking';
import { useCheckoutFlow } from 'typescript/context/checkout_flow';
import { getLegalUrls } from 'typescript/utilities/links';

interface DisclosureModalProps {
  submitOrder: () => Promise<void>;
  applicationFlowToken?: string;
}

const DisclosureModal = ({
  submitOrder,
  applicationFlowToken = null,
}: DisclosureModalProps) => {
  // TODO(bdang): Importing i18n fixes missing translations in this component,
  // temporary fix while we find a better solution.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { t, i18n } = useTranslation();
  const { trackingData } = useCheckoutFlow();
  const {
    disclosureDocumentToken,
    setDisclosureDocumentToken,
    showDisclosureModal,
    setShowDisclosureModal,
  } = usePaymentContext();

  if (!disclosureDocumentToken && !showDisclosureModal) {
    return null;
  }

  const legalUrls = getLegalUrls(applicationFlowToken);

  const disclosureDocumentUrl = `https://${getDomain('app.')}/api/v2/capital-shop/documents/${disclosureDocumentToken}/${getLocaleString()}`;

  const renderModalContent = () => {
    return (
      <>
        <h2 className="shop-grid-item-full">
          {disclosureDocumentToken
            ? t('checkout.payment_v3.disclosures.title')
            : t('checkout.payment_v3.disclosures.financingTitle')}
        </h2>
        <p
          className="shop-grid-item-full"
          dangerouslySetInnerHTML={{
            __html: t('checkout.payment_v3.disclosures.linkBody', legalUrls),
          }}
        />
        <p className="shop-grid-item-full">
          {t('checkout.payment_v3.disclosures.inquiryBody')}
        </p>
        {disclosureDocumentToken && (
          <p className="shop-grid-item-full">
            {t('checkout.payment_v3.disclosures.ctaBody')}
          </p>
        )}
      </>
    );
  };

  return (
    <MarketDialog isLoading={false} persistent={true}>
      <div className="shop-grid-container">
        <div className={`${styles.leftAlignedIcon} shop-grid-item-full`}>
          <MarketAccessory>
            <DisclosureIcon />
          </MarketAccessory>
        </div>
        {renderModalContent()}
        <div className={`${styles.buttonGroup} shop-grid-item-full`}>
          <MarketButtonGroup alignment="stack">
            {disclosureDocumentToken && (
              <MarketButton
                rank="secondary"
                href={disclosureDocumentUrl}
                target="_blank"
                onClick={() => {
                  onCheckoutPageClick({
                    eventDesc:
                      CHECKOUT_PAGE_CLICK_EVENTS.financing_disclosure_review,
                    trackingData,
                  });
                }}
              >
                {t('checkout.payment_v3.disclosures.reviewCTA')}
              </MarketButton>
            )}
            <MarketButton
              rank="primary"
              onClick={() => {
                onCheckoutPageClick({
                  eventDesc:
                    CHECKOUT_PAGE_CLICK_EVENTS.financing_disclosure_complete_order,
                  trackingData,
                });
                setShowDisclosureModal(false);
                setDisclosureDocumentToken(null);
                submitOrder();
              }}
            >
              {t('checkout.payment_v3.disclosures.completeCTA')}
            </MarketButton>
          </MarketButtonGroup>
        </div>
        <div className={`${styles.divider} shop-grid-item-full`}>
          <MarketDivider size="thin" />
        </div>
        <p className="shop-grid-item-full">
          {`${t('checkout.payment_v3.disclosures.notReady')} `}
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onCheckoutPageClick({
                eventDesc:
                  CHECKOUT_PAGE_CLICK_EVENTS.financing_disclosure_return_to_checkout,
                trackingData,
              });
              setShowDisclosureModal(false);
              setDisclosureDocumentToken(null);
            }}
          >
            {t('checkout.payment_v3.disclosures.returnCTA')}
          </a>
        </p>
      </div>
    </MarketDialog>
  );
};

export default DisclosureModal;
